import { Star, Quote } from "lucide-react";
import { useSite } from "@/lib/site";
import { Reveal } from "./common";

const SampleTag = ({ show }) => (show ? (
  <span className="inline-flex rounded-full bg-[#FF5733]/10 text-[#FF5733] px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest" data-testid="sample-reviews-tag">Sample reviews</span>
) : null);

export const Stars = ({ n, className = "w-4 h-4" }) => (
  <span className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <span key={i} className="relative">
        <Star className={`${className} text-slate-300`} />
        <span className="absolute inset-0 overflow-hidden" style={{ width: `${Math.max(0, Math.min(1, n - i + 1)) * 100}%` }}>
          <Star className={`${className} fill-amber-400 text-amber-400`} />
        </span>
      </span>
    ))}
  </span>
);

const ReviewCard = ({ r, dark, clone }) => (
  <figure className={`w-[300px] sm:w-[360px] shrink-0 rounded-3xl p-6 border ${dark ? "bg-white/5 border-white/10 text-white" : "bg-white border-slate-200"}`} data-testid={clone ? undefined : `review-card-${r.id}`}>
    <Stars n={r.rating} />
    <blockquote className={`mt-4 text-[15px] leading-relaxed ${dark ? "text-slate-300" : "text-slate-700"}`}>"{r.text}"</blockquote>
    <figcaption className="mt-5 flex items-center gap-3">
      <span className="grid place-items-center w-9 h-9 rounded-full bg-blue-600 text-white text-sm font-semibold">{r.name[0]}</span>
      <span><span className="block font-semibold text-sm">{r.name}</span><span className={`block text-xs ${dark ? "text-slate-500" : "text-slate-500"}`}>{r.role}</span></span>
    </figcaption>
  </figure>
);

const avgOf = (list) => (list.reduce((a, r) => a + r.rating, 0) / list.length).toFixed(1);

export const ReviewsMarquee = () => {
  const { reviews } = useSite();
  if (!reviews.length) return null;
  const avg = avgOf(reviews);
  const mid = Math.ceil(reviews.length / 2);
  const half = reviews.length >= 4 ? [reviews.slice(0, mid), reviews.slice(mid)] : [reviews];
  return (
    <section className="py-20 sm:py-28 overflow-hidden" data-testid="reviews-marquee">
      <Reveal className="max-w-7xl mx-auto px-5 sm:px-8 mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-4"><p className="overline">Client love</p><SampleTag show={reviews.some((x) => x.sample)} /></div>
          <h2 className="font-display text-3xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Businesses that <span className="text-blue-600">grew with us.</span></h2>
        </div>
        <div className="flex items-center gap-3"><Stars n={Number(avg)} className="w-5 h-5" /><span className="font-display font-bold text-xl">{avg}/5</span><span className="text-sm text-slate-500">from {reviews.length} review{reviews.length > 1 ? "s" : ""}</span></div>
      </Reveal>
      {half.map((row, k) => (
        <div key={row[0].id} className={`flex w-max gap-4 mb-4 marquee ${k ? "[animation-direction:reverse]" : ""}`}>
          {["a", "b"].map((copy) => (
            <div key={copy} className="flex gap-4" aria-hidden={copy === "b"}>
              {[...row, ...row].map((r, i) => <ReviewCard key={`${copy}-${r.id}-${i < row.length ? 1 : 2}`} r={r} clone={copy === "b" || i >= row.length} />)}
            </div>
          ))}
        </div>
      ))}
    </section>
  );
};

export const ReviewsGrid = () => {
  const { reviews } = useSite();
  if (!reviews.length) return null;
  const avg = avgOf(reviews);
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20" data-testid="reviews-grid">
      <div className="rounded-[2rem] bg-[#090D16] text-white p-6 sm:p-10 grain relative overflow-hidden">
        <div className="relative flex flex-col lg:flex-row gap-10">
          <div className="lg:w-1/3">
            <SampleTag show={reviews.some((x) => x.sample)} />
            <p className="mt-5 font-display text-6xl font-extrabold">{avg}</p>
            <Stars n={Number(avg)} className="w-5 h-5" />
            <p className="mt-3 text-slate-400 text-sm">Average rating from {reviews.length} client review{reviews.length > 1 ? "s" : ""}.</p>
          </div>
          <div className="lg:w-2/3 grid sm:grid-cols-2 gap-4">
            {reviews.slice(0, 4).map((r) => <ReviewCard key={r.id} r={r} dark />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export const ReviewQuote = ({ serviceId }) => {
  const { reviews } = useSite();
  const r = reviews.find((x) => x.service === serviceId) || reviews[0];
  if (!r) return null;
  return (
    <figure className="mt-4 rounded-3xl bg-white border border-slate-200 p-6" data-testid="review-quote">
      <div className="flex items-center justify-between"><Stars n={r.rating} /><SampleTag show={r.sample} /></div>
      <blockquote className="mt-4 text-slate-700 leading-relaxed">"{r.text}"</blockquote>
      <figcaption className="mt-4 text-sm"><span className="font-semibold">{r.name}</span> <span className="text-slate-500">· {r.role}</span></figcaption>
    </figure>
  );
};

export const ReviewSpotlight = () => {
  const { reviews } = useSite();
  const r = reviews.find((x) => x.featured) || reviews[0];
  if (!r) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28" data-testid="review-spotlight">
      <Reveal className="relative rounded-[2rem] bg-blue-50 border border-blue-100 p-8 sm:p-14">
        <Quote className="w-12 h-12 text-blue-600" />
        <blockquote className="mt-6 font-display text-2xl sm:text-4xl font-bold tracking-tight leading-snug text-slate-900 max-w-4xl">"{r.text}"</blockquote>
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Stars n={r.rating} />
          <p className="text-sm"><span className="font-semibold">{r.name}</span> <span className="text-slate-500">· {r.role}</span></p>
          <SampleTag show={r.sample} />
        </div>
      </Reveal>
    </section>
  );
};
